import React, { useMemo } from 'react';
import { Card, Descriptions, Tag, Space, Alert, Statistic, Row, Col, Typography } from 'antd';
import { SwapOutlined, BankOutlined, WarningOutlined } from '@ant-design/icons';
import { formatAmount, getActualLoanData, resolveEssPayload, toNumber } from './essPayload';

const { Text } = Typography;

interface ESSTakeoverInfoViewProps {
    data: any;
    formikProps?: any;
}

const ESSTakeoverInfoView: React.FC<ESSTakeoverInfoViewProps> = ({ data, formikProps }) => {
    const actualData = getActualLoanData(data, formikProps);

    // Check if this is a takeover loan
    const isTakeoverLoan = actualData?.loanType === 'TAKEOVER';

    // Parse ESS request payload
    const essPayload = useMemo(() => {
        return resolveEssPayload(actualData);
    }, [actualData?.essRequestPayload, actualData?.formattedEssRequestPayload]);

    if (!isTakeoverLoan || !essPayload) {
        return null;
    }

    const payoffAmount = toNumber(actualData?.payoffAmountToFsp1 || essPayload.takeOverAmount);
    const requestedAmount = toNumber(essPayload.requestedAmount);
    const totalLoanAmount = payoffAmount + requestedAmount;
    const netToEmployee = toNumber(actualData?.netDisbursementToEmployee || requestedAmount);

    return (
        <Card 
            title={
                <Space>
                    <SwapOutlined />
                    <span>Loan Takeover Information</span>
                </Space>
            }
            className="mb-4"
        >
            <Alert
                message="Loan Takeover Transaction"
                description="This is a loan takeover from another FSP. Please verify the previous FSP details and payoff amount before approval."
                type="warning"
                icon={<WarningOutlined />}
                showIcon
                className="mb-4"
            />

            {/* Takeover Financial Summary */}
            <Card type="inner" title="Takeover Financial Breakdown" className="mb-4">
                <Row gutter={16}>
                    <Col xs={24} sm={8}>
                        <Statistic 
                            title="Payoff to Previous FSP" 
                            value={payoffAmount}
                            precision={2}
                            prefix="TZS"
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic 
                            title="Additional Amount Requested" 
                            value={requestedAmount}
                            precision={2}
                            prefix="TZS"
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic 
                            title="Net to Employee" 
                            value={netToEmployee}
                            precision={2}
                            prefix="TZS"
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Col>
                </Row>
                
                <div className="mt-4">
                    <Text type="secondary">
                        Total Loan Amount: <strong>TZS {formatAmount(totalLoanAmount)}</strong> = 
                        Payoff (TZS {formatAmount(payoffAmount)}) + Additional (TZS {formatAmount(requestedAmount)})
                    </Text>
                </div>
            </Card>

            {/* Previous FSP Details */}
            <Card 
                type="inner"
                title={
                    <Space>
                        <BankOutlined />
                        <span>Previous FSP Details (FSP1)</span>
                    </Space>
                }
                className="mb-4"
            >
                <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
                    <Descriptions.Item label="Previous FSP Code">
                        <Tag color="red">{actualData?.previousFspCode || essPayload.fsp1Code || 'N/A'}</Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="Previous FSP Name">
                        {actualData?.previousFspName || essPayload.fsp1Name || 'N/A'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Previous Loan Number" span={2}>
                        <strong>{actualData?.previousLoanNumber || essPayload.fsp1LoanNumber || 'N/A'}</strong>
                    </Descriptions.Item>

                    <Descriptions.Item label="Outstanding Balance">
                        <strong>TZS {formatAmount(actualData?.previousOutstandingBalance || essPayload.fsp1OutstandingBalance || payoffAmount)}</strong>
                    </Descriptions.Item>

                    <Descriptions.Item label="Payoff Amount">
                        <Tag color="volcano">
                            TZS {formatAmount(payoffAmount)}
                        </Tag>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* FSP1 Payment Instructions */}
            <Card 
                type="inner"
                title="Payment Instructions to Previous FSP"
            >
                <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
                    <Descriptions.Item label="FSP1 Bank Account" span={2}>
                        <Text copyable><strong>{actualData?.fsp1BankAccount || essPayload.fsp1BankAccount || 'N/A'}</strong></Text>
                    </Descriptions.Item>

                    <Descriptions.Item label="Account Name" span={2}>
                        {actualData?.fsp1BankAccountName || essPayload.fsp1BankAccountName || 'N/A'}
                    </Descriptions.Item>

                    <Descriptions.Item label="SWIFT Code">
                        {actualData?.fsp1SwiftCode || essPayload.fsp1SwiftCode || 'N/A'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Payment Reference">
                        <Text copyable>{actualData?.fsp1PaymentReference || essPayload.fsp1PaymentReferenceNumber || 'N/A'}</Text>
                    </Descriptions.Item>
                </Descriptions>

                <Alert
                    message="Important: Disbursement Process"
                    description={
                        <div>
                            <p><strong>Step 1:</strong> Upon approval, TZS {formatAmount(payoffAmount)} must be paid to the previous FSP using the above bank details.</p>
                            <p><strong>Step 2:</strong> After payment confirmation, TZS {formatAmount(netToEmployee)} will be disbursed to the employee's account.</p>
                            <p><strong>Note:</strong> Both transactions must be completed for successful takeover.</p>
                        </div>
                    }
                    type="info"
                    showIcon
                    className="mt-4"
                />
            </Card>
        </Card>
    );
};

export default ESSTakeoverInfoView;
